import { createDownloadToast } from '@utils/importExport.utils';
import { useCallback } from 'react';
import { useResourceTokenMutation } from 'src/services/authent/security.api';

import { DownloadToastTypeEnum } from '@/types/download.types';

export type QueryParams = Record<string, number | boolean | string | string[]>;

const transformUrl = (url: string) => {
  if (url.includes('http')) {
    return url;
  }

  return `${window.location.origin}${url}`;
};

const buildDownloadUrl = (url: string, token: string, params?: QueryParams): URL => {
  const downloadUrl = new URL(transformUrl(url));
  if (params != undefined) {
    for (const [key, value] of Object.entries(params)) {
      downloadUrl.searchParams.set(key, String(value));
    }
  }
  downloadUrl.searchParams.set('token', token);
  return downloadUrl;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function handleDownloadUsingPicker(picker: any, url: URL, filename: string) {
  const handle = await picker({ suggestedName: filename });
  const writable = await handle.createWritable();
  try {
    const resp = await fetch(url);
    if (!resp.ok || !resp.body) {
      createDownloadToast(DownloadToastTypeEnum.ERROR);
    } else {
      createDownloadToast(DownloadToastTypeEnum.SUCCESS);
    }
    await resp.body?.pipeTo(writable);
  } catch (e) {
    createDownloadToast(DownloadToastTypeEnum.ERROR);
    await writable.abort().catch(() => {
      // do nothing
    });
    if (!(e instanceof DOMException && e.name === 'AbortError')) {
      throw e;
    }
  }
}

function handleFallbackDownload(url: URL) {
  console.warn('File System Access API is not supported by this browser, or this connection is not secured');
  window.open(url, '_blank', 'noopener,noreferrer');
  createDownloadToast(DownloadToastTypeEnum.SUCCESS);
}

export function useNativeDownload() {
  const [fetchResourceToken, status] = useResourceTokenMutation();

  const startDownload = useCallback(
    async (url: string, params?: QueryParams, filename = 'export.zip') => {
      const { resourceToken } = await fetchResourceToken().unwrap();
      const downloadUrl = buildDownloadUrl(url, resourceToken ?? '', params);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const picker = (window as any).showSaveFilePicker;
      if (picker) {
        await handleDownloadUsingPicker(picker, downloadUrl, filename);
      } else {
        handleFallbackDownload(downloadUrl);
      }
    },
    [fetchResourceToken],
  );

  return {
    startDownload,
    isLoading: status.isLoading,
    isError: status.isError,
  };
}
